import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlayCircle, Settings } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const BacktestForm = ({ portfolioHoldings, onRunBacktest, isLoading }) => {
  const location = useLocation();
  const [initialCapital, setInitialCapital] = useState(10000);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [timeframe, setTimeframe] = useState('5y');
  const [rebalanceFrequency, setRebalanceFrequency] = useState('quarterly');
  const [allocationMode, setAllocationMode] = useState('equal');
  const [customWeights, setCustomWeights] = useState({});
  const [manualTickers, setManualTickers] = useState('');

  // Initialize from screening page selection or portfolio
  useEffect(() => {
    if (location.state?.selectedStocks) {
      // Pre-populate from screening page
      const tickers = location.state.selectedStocks;
      setSelectedStocks(tickers);
      setManualTickers(tickers.join(', '));
      
      // Initialize equal weights
      const weights = {};
      tickers.forEach(ticker => {
        weights[ticker] = (100 / tickers.length).toFixed(2);
      });
      setCustomWeights(weights);
    } else if (portfolioHoldings && portfolioHoldings.length > 0) {
      // Initialize from portfolio
      const tickers = portfolioHoldings.slice(0, 5).map(h => h.ticker);
      setSelectedStocks(tickers);
      
      // Initialize equal weights
      const weights = {};
      tickers.forEach(ticker => {
        weights[ticker] = (100 / tickers.length).toFixed(2);
      });
      setCustomWeights(weights);
    }
  }, [portfolioHoldings, location.state]);

  const handleStockSelection = (ticker, isChecked) => {
    if (isChecked) {
      if (selectedStocks.length < 5) {
        setSelectedStocks([...selectedStocks, ticker]);
      }
    } else {
      setSelectedStocks(selectedStocks.filter(t => t !== ticker));
    }
  };

  const handleManualTickersChange = (e) => {
    const value = e.target.value;
    setManualTickers(value);
    
    // Parse comma-separated tickers
    const tickers = value.split(',').map(t => t.trim().toUpperCase()).filter(t => t.length > 0);
    if (tickers.length <= 5) {
      setSelectedStocks(tickers);
    }
  };

  const handleWeightChange = (ticker, weight) => {
    setCustomWeights({
      ...customWeights,
      [ticker]: weight
    });
  };

  const handleRunBacktest = () => {
    // Prepare weights
    let weights = {};
    
    if (allocationMode === 'equal') {
      selectedStocks.forEach(ticker => {
        weights[ticker] = 1 / selectedStocks.length;
      });
    } else {
      // Normalize custom weights
      const totalWeight = Object.values(customWeights).reduce((sum, w) => sum + parseFloat(w || 0), 0);
      selectedStocks.forEach(ticker => {
        weights[ticker] = parseFloat(customWeights[ticker] || 0) / totalWeight;
      });
    }

    onRunBacktest({
      initialCapital,
      tickers: selectedStocks,
      timeframe,
      rebalanceFrequency,
      weights
    });
  };

  const isValid = selectedStocks.length > 0 && selectedStocks.length <= 5 && initialCapital > 0;

  return (
    <Card className="p-6 bg-slate-800/90 border-slate-700" data-testid="backtest-form">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="w-6 h-6 text-blue-400" />
        <h2 className="text-2xl font-bold text-white">Backtest Configuration</h2>
      </div>

      <div className="space-y-6">
        {/* Initial Capital */}
        <div>
          <Label htmlFor="initial-capital" className="text-base font-semibold mb-2 block text-gray-300">
            Initial Capital ($)
          </Label>
          <Input
            id="initial-capital"
            type="number"
            value={initialCapital}
            onChange={(e) => setInitialCapital(parseFloat(e.target.value))}
            min={1000}
            step={1000}
            className="text-lg bg-slate-700/50 border-slate-600 text-white"
            data-testid="initial-capital-input"
          />
        </div>

        {/* Stock Selection */}
        <div>
          <Label className="text-base font-semibold mb-2 block text-gray-300">
            Stock Selection (Max 5)
          </Label>
          
          {portfolioHoldings && portfolioHoldings.length > 0 ? (
            <div className="space-y-2 mb-3">
              <p className="text-sm text-gray-400 mb-2">Select from portfolio:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {portfolioHoldings.map(holding => (
                  <label
                    key={holding.ticker}
                    className="flex items-center space-x-2 p-2 border border-slate-600 rounded cursor-pointer hover:bg-slate-700 bg-slate-700/50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStocks.includes(holding.ticker)}
                      onChange={(e) => handleStockSelection(holding.ticker, e.target.checked)}
                      disabled={!selectedStocks.includes(holding.ticker) && selectedStocks.length >= 5}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-white">{holding.ticker}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-3">
            <Label htmlFor="manual-tickers" className="text-sm mb-1 block text-gray-400">
              Or enter tickers manually (comma-separated):
            </Label>
            <Input
              id="manual-tickers"
              type="text"
              placeholder="e.g., AAPL, MSFT, GOOGL"
              value={manualTickers}
              onChange={handleManualTickersChange}
              data-testid="manual-tickers-input"
              className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-500"
            />
          </div>

          <div className="mt-2">
            <p className="text-sm text-gray-300">
              Selected: <span className="font-semibold text-white">{selectedStocks.join(', ') || 'None'}</span>
            </p>
          </div>
        </div>

        {/* Timeframe */}
        <div>
          <Label className="text-base font-semibold mb-2 block text-gray-300">Timeframe</Label>
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger data-testid="timeframe-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1y">1 Year</SelectItem>
              <SelectItem value="3y">3 Years</SelectItem>
              <SelectItem value="5y">5 Years</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Rebalance Frequency */}
        <div>
          <Label className="text-base font-semibold mb-2 block text-gray-300">Rebalance Frequency</Label>
          <Select value={rebalanceFrequency} onValueChange={setRebalanceFrequency}>
            <SelectTrigger data-testid="rebalance-frequency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annually">Annually</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Allocation Mode */}
        <div>
          <Label className="text-base font-semibold mb-2 block text-gray-300">Allocation Mode</Label>
          <Select value={allocationMode} onValueChange={setAllocationMode}>
            <SelectTrigger data-testid="allocation-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="equal">Equal Weight</SelectItem>
              <SelectItem value="custom">Custom Weights</SelectItem>
            </SelectContent>
          </Select>

          {/* Custom Weights Input */}
          {allocationMode === 'custom' && selectedStocks.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-gray-400 mb-2">Custom weights (%):</p>
              {selectedStocks.map(ticker => (
                <div key={ticker} className="flex items-center gap-2">
                  <Label className="w-20 text-sm text-white">{ticker}:</Label>
                  <Input
                    type="number"
                    value={customWeights[ticker] || 0}
                    onChange={(e) => handleWeightChange(ticker, e.target.value)}
                    min={0}
                    max={100}
                    step={1}
                    className="flex-1"
                  />
                  <span className="text-sm text-gray-300">%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Run Button */}
        <Button
          onClick={handleRunBacktest}
          disabled={!isValid || isLoading}
          className="w-full h-12 text-lg"
          data-testid="run-backtest-button"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Running Backtest...
            </>
          ) : (
            <>
              <PlayCircle className="w-5 h-5 mr-2" />
              Run Backtest
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default BacktestForm;
